import { useEffect, useState } from "react";
import { api, MARKET_ORDER, MARKET_META } from "../lib/api";
import { Calendar as CalendarIcon, Search } from "lucide-react";
import { useSeo } from "../lib/useSeo";

const prettyDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const PreviousResults = () => {
    useSeo({
        title: "Previous Shillong Morning Teer Results | FR & SR Chart Archive",
        description: "Browse the complete archive of previous Shillong Morning Teer results — filter by date and market to view FR and SR winning numbers of Khanapara, Juwai, Bhutan and more.",
        keywords: "shillong morning teer previous result, teer result chart, teer archive, khanapara previous result",
        path: "/previous-results",
        breadcrumbs: [
            { name: "Home", path: "/" },
            { name: "Previous Results", path: "/previous-results" },
        ],
    });

    const [date, setDate] = useState("");
    const [market, setMarket] = useState("all");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (date) params.date = date;
            if (market !== "all") params.market_key = market;
            const { data } = await api.get("/results", { params });
            setRows(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [date, market]);

    return (
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest text-yellow-600 font-semibold">Archive</div>
                    <h1 className="font-display font-bold text-4xl md:text-5xl text-blue-950 tracking-tight mt-1">
                        Previous Results
                    </h1>
                    <p className="text-slate-500 mt-2 text-base">
                        Purani tareekh ke Teer results yahaan search karein.
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="mt-8 card-crisp p-4 md:p-5 flex flex-col md:flex-row gap-3 md:items-center">
                <div className="flex-1 flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2">
                    <CalendarIcon className="h-4 w-4 text-slate-500" />
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        data-testid="filter-date"
                        className="w-full bg-transparent outline-none text-sm font-num"
                    />
                    {date && (
                        <button
                            onClick={() => setDate("")}
                            className="text-xs text-blue-900 font-semibold"
                            data-testid="clear-date-btn"
                        >
                            Clear
                        </button>
                    )}
                </div>

                <select
                    value={market}
                    onChange={(e) => setMarket(e.target.value)}
                    data-testid="filter-market"
                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
                >
                    <option value="all">All Markets</option>
                    {MARKET_ORDER.map((k) => (
                        <option key={k} value={k}>{MARKET_META[k].name}</option>
                    ))}
                </select>

                <button
                    onClick={load}
                    className="btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
                    data-testid="apply-filter-btn"
                >
                    <Search className="h-4 w-4" /> Search
                </button>
            </div>

            {/* Table */}
            <div className="mt-6 card-crisp overflow-hidden">
                <div className="grid grid-cols-12 bg-blue-900 text-white text-xs uppercase tracking-widest font-semibold px-4 py-3">
                    <div className="col-span-4 md:col-span-3">Date</div>
                    <div className="col-span-4 md:col-span-5">Market</div>
                    <div className="col-span-2 text-right">F/R</div>
                    <div className="col-span-2 text-right">S/R</div>
                </div>
                {loading ? (
                    <div className="p-6 text-center text-slate-500 text-sm">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-center text-slate-400" data-testid="no-results-msg">
                        Koi results nahi mile. Filter change karein.
                    </div>
                ) : (
                    <ul>
                        {rows.map((r) => {
                            const meta = MARKET_META[r.market_key];
                            return (
                                <li
                                    key={r.id}
                                    className="grid grid-cols-12 px-4 py-3 items-center border-t border-slate-100 hover:bg-slate-50 transition-colors"
                                    data-testid={`row-${r.id}`}
                                >
                                    <div className="col-span-4 md:col-span-3 font-num text-sm text-slate-700">
                                        {prettyDate(r.date)}
                                    </div>
                                    <div className="col-span-4 md:col-span-5 flex items-center gap-2 text-sm">
                                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: meta?.accent }} />
                                        <span className="font-semibold text-blue-950">{meta?.name || r.market_key}</span>
                                    </div>
                                    <div className="col-span-2 text-right result-digit text-lg text-blue-900">
                                        {r.fr ?? "XX"}
                                    </div>
                                    <div className="col-span-2 text-right result-digit text-lg text-blue-900">
                                        {r.sr ?? "XX"}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default PreviousResults;
